import {
  Box,
  Center,
  Flex,
  Icon,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  DownloadCloud,
  Link as LinkIcon,
  X,
  ZoomIn,
  ZoomOut,
} from "react-feather";
import { useTranslation } from "react-i18next";

import AudioVideoPlayer from "@/components/common/AudioVideoPlayer";
import FileTag from "@/components/common/FileTag";
import { ImageAutoHeight, ImageAutoWidth } from "@/components/common/Image";
import { useToastPromise } from "@/hooks/useToast";
import { useUser } from "@/hooks/useUser";
import { CloudType, File } from "@/types/cloud";
import { formatBytes } from "@/utils/filesize";
import { Document, Page, pdfjs } from "react-pdf";
import {
  getFileUri,
  getCloudPreviewUriToCopy,
  downloadFile,
} from "@/utils/helpers";
import useIsMobile from "@/hooks/useIsMobile";

type Props = {
  file: File;
  isOpen: boolean;
  onClose: () => void;
  preview?: boolean;
};

const PreviewModal = ({
  file,
  isOpen,
  onClose,
  preview = false,
}: Props): React.ReactElement => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={
        file?.type === CloudType.OTHER && file?.mimetype == "application/pdf"
          ? "full"
          : "3xl"
      }
      scrollBehavior="outside"
      preserveScrollBarGap
    >
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent
        pos="initial"
        bg="transparent"
        //  margin={0} rounded="none"
      >
        <ModalBody d="inherit" justifyContent="center" alignItems="center">
          <Box
            d="inline-flex"
            flexDir="column"
            alignItems="center"
            padding="2rem 2rem"
            bg="rgba(255, 255, 255, 0.2)"
            backdropFilter="blur(5px)"
            borderRadius=".375rem"
            border="1px solid rgba(255, 255, 255, 0.3)"
            backgroundClip="border-box"
            color="white"
            boxShadow="0 3px 5px rgb(0,0,0,0.03)"
            w={file?.mimetype == "application/pdf" ? "100vw" : null}
            minH={file?.mimetype == "application/pdf" ? "95vh" : null}
            h="fit-content"
          >
            <IconButton
              position="absolute"
              top={5}
              right={5}
              aria-label="button"
              icon={<X />}
              onClick={onClose}
            />
            <FilePreview isWebsite={preview} file={file} />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type FilePreviewProps = {
  file: File;
  download?: boolean;
  isWebsite?: boolean;
  isLoading?: boolean;
};

export const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  download = false,
  isWebsite = false,
  isLoading = false,
}: FilePreviewProps) => {
  const { t } = useTranslation("common");
  const fileName = file?.name || file?.originalname;
  const toastPromise = useToastPromise();
  const [session] = useUser();
  const [documentScale, setDocumentScale] = useState<number>(1);
  const [numPages, setNumPages] = React.useState(null);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);
  const isMobile = useIsMobile();

  const saveFile = async () => {
    await downloadFile(getFileUri(file?.path), file?.name);
  };

  const zoomIn = () => {
    setDocumentScale(documentScale == 2 ? 2 : documentScale + 0.2);
  };

  const zoomOut = () => {
    setDocumentScale(documentScale == 1 ? 1 : documentScale - 0.2);
  };

  useEffect(() => {
    if (download && file) saveFile();
  }, [download, file]);

  if (isLoading)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  if (!file) return <Text textAlign="center">{t("cloud.fileNotFound")}</Text>;

  const File = (
    <>
      {file?.type === CloudType.PHOTO && (
        <>
          {!isMobile && (
            <Box mt={isWebsite ? 3 : 5} mb={5} maxHeight="calc(100vh - 224px)">
              <ImageAutoWidth
                src={getFileUri(file?.path)}
                layout="fill"
                alt=""
              />
            </Box>
          )}
          {isMobile && (
            <Box mt={5} mb={5}>
              <ImageAutoHeight
                src={getFileUri(file?.path)}
                layout="fill"
                alt=""
              />
            </Box>
          )}
          {!isWebsite && (
            <Link
              target="_blank"
              href={getFileUri(file?.path)}
              rel="noopener noreferrer"
            >
              {t("cloud.showImage")}
            </Link>
          )}
        </>
      )}
      {(file?.type === CloudType.VIDEO || file?.type === CloudType.AUDIO) && (
        <Box
          h={isWebsite ? "90%" : "100%"}
          maxH={isWebsite ? "90%" : "100%"}
          mt={isWebsite ? 10 : 5}
          mb={5}
          w={file?.type === CloudType.AUDIO ? "100%" : "auto"}
        >
          <AudioVideoPlayer
            file={file}
            downloadAvailable
            isWebsite={isWebsite}
            autoStart={!isWebsite}
          />
        </Box>
      )}
      {file?.type === CloudType.OTHER &&
        file?.mimetype == "application/pdf" && (
          <Flex
            direction="column"
            alignItems="center"
            h={"100%"}
            maxH={"100%"}
            w={"100%"}
          >
            <Flex
              mb={5}
              justifyContent="center"
              gap={5}
              w="100%"
              alignItems="center"
            >
              {numPages && numPages > 1 && (
                <IconButton
                  aria-label="granted-button"
                  icon={<ArrowLeft />}
                  disabled={pageNumber === 1}
                  onClick={() => setPageNumber(pageNumber - 1)}
                />
              )}
              <IconButton
                aria-label="button"
                icon={<ZoomIn />}
                disabled={documentScale >= 2}
                onClick={zoomIn}
              />
              <Box>
                <Text>{Math.round(documentScale * 100)}%</Text>
              </Box>
              <IconButton
                aria-label="button"
                icon={<ZoomOut />}
                disabled={documentScale <= 1}
                onClick={zoomOut}
              />
              {numPages && numPages > 1 && (
                <IconButton
                  aria-label="granted-button"
                  icon={<ArrowRight />}
                  disabled={pageNumber === numPages}
                  onClick={() => setPageNumber(pageNumber + 1)}
                />
              )}
            </Flex>
            <Document
              file={getFileUri(file?.path)}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={() => (
                <Center>
                  <Spinner />
                </Center>
              )}
            >
              <Page
                pageNumber={pageNumber}
                renderTextLayer={false}
                loading=""
                renderAnnotationLayer={false}
                scale={documentScale}
              />
            </Document>
          </Flex>
        )}
    </>
  );

  return (
    <>
      <Box>
        <Text
          isTruncated
          maxW="calc(100vw - 96px)"
          textAlign="center"
          mb="0.5rem"
        >
          {fileName} ({formatBytes(file?.filesize)})
          <Box d="inline-flex" gap="1rem" ml="1rem" verticalAlign={-5}>
            <Tooltip hasArrow label={t("copyToClipboard")} placement="top">
              <Icon
                as={LinkIcon}
                fontSize="20px"
                cursor="pointer"
                onClick={() => {
                  toastPromise.promise(
                    navigator.clipboard.writeText(
                      getCloudPreviewUriToCopy(file?.filename)
                    ),
                    {
                      success: t("copyToClipboardSuccess"),
                      error: t("copyToClipboardError"),
                    }
                  );
                }}
              />
            </Tooltip>
            <Tooltip
              hasArrow
              label={
                session?.user?.id
                  ? t("cloud.download")
                  : t("cloud.loginToDownload")
              }
              placement="top"
            >
              <Icon
                as={DownloadCloud}
                fontSize="20px"
                opacity={session?.user?.id ? 1 : 0.3}
                cursor={session?.user?.id ? "pointer" : null}
                onClick={session?.user?.id ? saveFile : null}
              />
            </Tooltip>
          </Box>
        </Text>
      </Box>

      <Flex mt={2} gridGap={2} flexWrap="wrap" justifyContent="center">
        {file?.tags?.map((tag) => (
          <FileTag
            key={tag?.name}
            name={tag?.name}
            hideName
            colorScheme={tag?.tagGroup?.colorScheme}
            picture={tag?.picture}
            size="md"
          />
        ))}
      </Flex>

      {File}
    </>
  );
};

export default PreviewModal;
